import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { SelectDialogSearchField } from '../../../common/dialogs/SelectDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useGetChangeOrderList } from '../ChangeOrder.hook';
import { ChangeOrder } from '../ChangeOrder.interface';

const StyledDialogContent = styled(DialogContent)`
  padding: 8px 0 0;
  .pagination {
    bottom: 0;
    position: sticky;
    background-color: #fff;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  .row-divider {
    background-color: ${props => props.theme.palette.grey[100]};
    .MuiTableCell-root {
      padding-top: 0px;
      padding-bottom: 0px;
    }
  }
`;

const columns: { key: string; label: string }[] = [
  { key: 'changeOrder.id', label: 'ID' },
  { key: 'changeOrder.name', label: 'Name' },
  { key: 'task.status', label: 'Status' }
];

export interface AssignChangeOrderDialogProps {
  items?: ChangeOrder[];
  open: boolean;
  title?: React.ReactNode;
  content?: React.ReactNode;
  searchLabel?: string;
  onCancel?: () => void;
  onAssign?: (items: ChangeOrder[]) => void;
}

const AssignChangeOrderDialog: React.FC<
  AssignChangeOrderDialogProps
> = props => {
  const {
    items,
    open,
    title = 'Assign Change Order',
    searchLabel = 'Search Item(s)',
    content = 'Search for Change Order to assign.',
    onCancel,
    onAssign
  } = props;

  const defaultLimit = 5;
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [order, setOrder] = useState('changeOrder.id');
  const [direction, setDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(items || []);
  const { data, isLoading, error, getChangeOrders } = useGetChangeOrderList();
  const colSpan = columns.length + 1;

  const changeOrders = useMemo(() => data?.data || [], [data]);

  const isActive = (column: string) => column === order;

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return isActive(column) && direction === 'DESC' ? 'desc' : 'asc';
  };

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const findChangeOrdersWithParams = useCallback(() => {
    getChangeOrders({ page, limit, order, direction, search });
  }, [page, limit, order, direction, search, getChangeOrders]);

  // clear state
  useEffect(() => {
    if (!open) {
      setPage(0);
      setLimit(defaultLimit);
      setSearch('');
      setSelected([]);
    }
  }, [open]);

  useEffect(() => {
    open && findChangeOrdersWithParams();
  }, [open, findChangeOrdersWithParams]);

  useEffect(() => {
    items && setSelected(items);
  }, [items]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <StyledDialogContent>
        <Box mx={3}>
          <DialogContentText>{content}</DialogContentText>
          <SelectDialogSearchField
            label={searchLabel}
            onSearch={value => {
              setPage(0);
              setSearch(value);
            }}
          />
        </Box>
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                {columns.map((column, index) => (
                  <TableCell key={`${column.key}-${index}`}>
                    <TableSortLabel
                      active={isActive(column.key)}
                      direction={getSortDirection(column.key)}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell padding='none' colSpan={colSpan}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {changeOrders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colSpan}>
                    <Typography variant='body1'>
                      {isLoading
                        ? 'Loading change orders...'
                        : 'No available change orders.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {error && (
                <TableRow>
                  <TableCell padding='none' colSpan={colSpan}>
                    <Alert severity='error'>{error.message}</Alert>
                  </TableCell>
                </TableRow>
              )}
              {changeOrders.map((item, index) => (
                <TableRow
                  hover
                  color='primary'
                  key={`${item.id}-${index}`}
                  onClick={() => {
                    setSelected(selected => {
                      const copy = Array.from(selected);
                      const index = copy.findIndex(co => co.id === item.id);
                      if (index > -1) {
                        copy.splice(index, 1);
                      } else {
                        copy.push(item);
                      }
                      return copy;
                    });
                  }}
                >
                  <TableCell padding='checkbox'>
                    <Checkbox
                      color='primary'
                      checked={selected.some(co => co.id === item.id)}
                    />
                  </TableCell>
                  <TableCell>{item.task?.key || `CO-${item.id}`}</TableCell>
                  <WrappedTableCell>{item.name}</WrappedTableCell>
                  <TableCell>
                    <TaskStatusLabel task={item.task} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
        <TablePagination
          className='pagination'
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={data?.total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={(_, page) => setPage(page)}
          onRowsPerPageChange={event => {
            setLimit(+event.target.value);
            setPage(0);
          }}
        />
      </StyledDialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => onAssign?.(selected)}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignChangeOrderDialog;
